import React, { useCallback, useEffect } from 'react';
import { usePublishRequestable } from 'services/usePublishRequestable';
import { SteeringIndicator } from './SteeringIndicator';
import { SpeedMeter } from './SpeedMeter';
import { DriveInfo } from './DriveInfo';
import { DriveSwitch } from './DriveSwitch';
import { useMainScreenLayout } from 'services/useCameraLayout';
import { useChannelLayout } from 'components/containers/common/VehicleMonitor/useChannelLayout';
import { AssignedCamera } from 'core/models/layout';
import { EightViewsMatrix } from 'components/views/molecules/DriveModeLayout';
import { ControllerPanel } from 'components/views/organisms/ControllerPanel';
import { VideoPlayer } from 'components/containers/common/VehicleMonitor/VideoPlayer';
import { useChannelFromTag } from 'services/useChannelFromTag';
import { VehicleStatus } from 'components/containers/common/VehicleMonitor/VehicleStatus';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { AlertTicker } from 'components/views/molecules/AlertTicker';
import { useStandBy, useHeartbeatHealth } from 'services/useStandBy';
import { useScopeCheck } from 'services/useScope';
import { useExtraFromSessionStorage, useHeartbeatFrequency, useHeartbeatTimeout } from 'services/useSubChannels';

export type EightViewsForDriveModeLayout = {
    views: EightViewsMatrix;
    controllerPanel: React.ReactNode;
}

export function useEightViewsForDriveModeLayout(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, alerting: 'green' | 'red', alertMessages: string[]): EightViewsForDriveModeLayout {
    const viewOnly = !usePublishRequestable();

    const [displayLayout] = useMainScreenLayout(vehicleId);

    const layout = useChannelLayout(vehicleId, projectId, environmentId);

    function cameraMonitorIfExist(camera: AssignedCamera | null, audio = false) {
        const location = camera?.tag;
        const mirror = camera?.mirror ?? false;
        const upsidedown = camera?.upsidedown ?? false;
        const channel = location && layout
            ? layout.cameras.get(location)
            : undefined;
        return channel
            ? <VideoPlayer channel={channel} mirror={mirror} upsidedown={upsidedown} audio={audio} viewOnly={viewOnly} projectId={projectId} environmentId={environmentId} stretch={true} />
            : null;
    }

    const microphoneChannel = useChannelFromTag(displayLayout.microphone?.tag, layout);

    function firstThreeItems(targets: AssignedCamera[]) {
        switch (targets.length) {
            case 0:
                return [null, null, null];
            case 1:
                return [null, targets[0], null];
            case 2:
                return [null, targets[0], targets[1]];
            default:
                return targets.slice(0, 3);
        }
    }

    function firstItem(targets: AssignedCamera[]) {
        switch (targets.length) {
            case 0:
                return null;
            default:
                return targets[0];
        }
    }

    const tops = firstThreeItems(displayLayout.top);
    const centers = firstThreeItems(displayLayout.center);
    const eightViewsMode = centers.filter(x => !!x).length > 1;

    const vehicleStatus = <VehicleStatus vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} channel={microphoneChannel} />;

    const { extra, setExtra } = useExtraFromSessionStorage();
    const heartbeatFrequency = useHeartbeatFrequency(extra, setExtra);
    const heartbeatTimeout = useHeartbeatTimeout(extra, setExtra);

    const [standby, setStandBy] = useStandBy(vehicleId, projectId, heartbeatFrequency);
    const onStandBy = useCallback(() => {
        setStandBy(prev => !prev);
    }, [setStandBy]);
    const heartbeatStatus = useHeartbeatHealth(vehicleId, projectId, standby, heartbeatFrequency, heartbeatTimeout);
    const isControlAllowed = useScopeCheck('sendVehicleCommandScope') ?? false;
    const isEmergencyAllowed = useScopeCheck('sendEmergencyStopCommandScope') ?? false;

    useEffect(() => {
        console.info('Autoware heartbeat status', heartbeatStatus.status);
    }, [heartbeatStatus.status]);

    return {
        views: [
            eightViewsMode
                ?
                [
                    cameraMonitorIfExist(tops[0]),
                    cameraMonitorIfExist(tops[1]),
                    cameraMonitorIfExist(tops[2]),
                ]
                : [
                    vehicleStatus,
                    cameraMonitorIfExist(tops[1]),
                    cameraMonitorIfExist(tops[2]),
                ],
            eightViewsMode
                ? [
                    cameraMonitorIfExist(centers[0]),
                    cameraMonitorIfExist(centers[1]),
                    cameraMonitorIfExist(centers[2]),
                ]
                : [
                    cameraMonitorIfExist(firstItem(displayLayout.left)),
                    cameraMonitorIfExist(centers[1]),
                    cameraMonitorIfExist(firstItem(displayLayout.right)),
                ],
            eightViewsMode
                ? [
                    cameraMonitorIfExist(firstItem(displayLayout.left)),
                    cameraMonitorIfExist(firstItem(displayLayout.right)),
                ]
                : undefined,
        ],
        controllerPanel: <ControllerPanel
            vehicleStatus={eightViewsMode ? vehicleStatus : undefined}
            steeringIndicator={< SteeringIndicator vehicleId={vehicleId} projectId={projectId} />}
            speedMeter={< SpeedMeter vehicleId={vehicleId} projectId={projectId} />}
            driveInfo={< DriveInfo vehicleId={vehicleId} projectId={projectId} />}
            driveSwitch={< DriveSwitch vehicleId={vehicleId} projectId={projectId} />}
            alertTicker={<AlertTicker caption='Stop the vehicle immediately! You have several critical problems!' messages={alertMessages} />}
            alerting={alerting}
            standby={standby}
            onStandBy={onStandBy}
            heartbeatStatus={heartbeatStatus}
            isStandByAllowed={isControlAllowed || isEmergencyAllowed}
        />
    };
}
